import React, { useState, useEffect, useRef } from 'react';
import { Camera, X, AlertTriangle, CheckCircle, Sun, Eye, Zap, Timer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Enhanced Camera Capture Component with Quality Guidance & Burst Mode
 * 
 * Features:
 * - Real-time blur detection
 * - Low light detection
 * - Framing guidance
 * - Auto best-frame selection (single capture with mini-buffer)
 * - Short burst mode (2-3 seconds)
 * 
 * @param {Object} props
 * @param {Function} props.onCapture - Callback with captured image data URL
 * @param {Function} props.onCancel - Callback when cancelled
 */
const EnhancedCameraCapture = ({ onCapture, onCancel }) => {
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [captureMode, setCaptureMode] = useState('single'); // 'single' | 'burst'
  const [burstDuration, setBurstDuration] = useState(2); // 2 or 3 seconds
  const [isCapturing, setIsCapturing] = useState(false);
  const [burstProgress, setBurstProgress] = useState(0);
  const [showGuidance, setShowGuidance] = useState(true);
  
  // Quality metrics
  const [qualityMetrics, setQualityMetrics] = useState({
    blur: 'checking',
    brightness: 'checking',
    framing: 'checking'
  });
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const qualityCheckInterval = useRef(null);
  const burstInterval = useRef(null);
  
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);
  
  useEffect(() => {
    if (cameraStream && videoRef.current && showGuidance) {
      // Start quality checking at 2-3 FPS
      qualityCheckInterval.current = setInterval(() => {
        checkFrameQuality();
      }, 400); // ~2.5 FPS
      
      return () => {
        if (qualityCheckInterval.current) {
          clearInterval(qualityCheckInterval.current);
        }
      };
    }
  }, [cameraStream, showGuidance]);
  
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraStream(stream);
    } catch (error) {
      console.error('Camera access error:', error);
      toast.error('Failed to access camera. Please check permissions.');
    }
  };
  
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (qualityCheckInterval.current) {
      clearInterval(qualityCheckInterval.current);
    }
    if (burstInterval.current) {
      clearInterval(burstInterval.current);
    }
  };
  
  /**
   * Check frame quality metrics (blur, brightness, framing)
   * Client-side lightweight implementation
   */
  const checkFrameQuality = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Use small canvas for quality check (faster)
    const checkWidth = 320;
    const checkHeight = 240;
    canvas.width = checkWidth;
    canvas.height = checkHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, checkWidth, checkHeight);
    
    const imageData = ctx.getImageData(0, 0, checkWidth, checkHeight);
    const pixels = imageData.data;
    
    // 1. Blur Detection (variance of Laplacian approximation)
    const blurScore = calculateBlurScore(pixels, checkWidth, checkHeight);
    
    // 2. Brightness Detection
    const brightnessScore = calculateBrightness(pixels);
    
    // 3. Framing Detection (edge density)
    const framingScore = calculateFraming(pixels, checkWidth, checkHeight);
    
    setQualityMetrics({
      blur: blurScore > 100 ? 'sharp' : blurScore > 50 ? 'slight_blur' : 'blurry',
      brightness: brightnessScore > 50 && brightnessScore < 200 ? 'good' : brightnessScore <= 50 ? 'dark' : 'bright',
      framing: framingScore > 0.3 ? 'good' : 'too_far'
    });
  };
  
  /**
   * Calculate blur score using variance of Laplacian
   * Higher score = sharper image
   */
  const calculateBlurScore = (pixels, width, height) => {
    let sum = 0;
    let count = 0;
    
    // Convert to grayscale and calculate gradients
    const gray = [];
    for (let i = 0; i < pixels.length; i += 4) {
      gray.push(0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]);
    }
    
    // Simplified Laplacian variance
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const laplacian = Math.abs(
          -gray[idx - width] - gray[idx - 1] + 4 * gray[idx] - gray[idx + 1] - gray[idx + width]
        );
        sum += laplacian * laplacian;
        count++;
      }
    }
    
    return count > 0 ? Math.sqrt(sum / count) : 0;
  };
  
  /**
   * Calculate average brightness
   */
  const calculateBrightness = (pixels) => {
    let sum = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      sum += 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
    }
    return sum / (pixels.length / 4);
  };
  
  /**
   * Calculate framing score (edge density as proxy for content)
   */
  const calculateFraming = (pixels, width, height) => {
    let edgeCount = 0;
    const threshold = 30;
    
    for (let i = 0; i < pixels.length - 4; i += 4) {
      const diff = Math.abs(pixels[i] - pixels[i + 4]) + 
                   Math.abs(pixels[i + 1] - pixels[i + 5]) + 
                   Math.abs(pixels[i + 2] - pixels[i + 6]);
      if (diff > threshold) edgeCount++;
    }
    
    return edgeCount / (pixels.length / 4);
  };
  
  /**
   * Capture a single frame with full resolution
   */
  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Capture at full video resolution
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.92);
    const blurScore = calculateBlurScore(
      ctx.getImageData(0, 0, canvas.width, canvas.height).data,
      canvas.width,
      canvas.height
    );
    const brightnessScore = calculateBrightness(
      ctx.getImageData(0, 0, canvas.width, canvas.height).data
    );
    
    return {
      dataUrl: imageData,
      blurScore,
      brightnessScore,
      timestamp: Date.now()
    };
  };
  
  /**
   * Handle single capture with mini-buffer for best frame selection
   */
  const handleSingleCapture = async () => {
    setIsCapturing(true);
    const frames = [];
    
    try {
      // Capture 8 frames over ~0.8 seconds
      for (let i = 0; i < 8; i++) {
        const frame = captureFrame();
        if (frame) frames.push(frame);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Select best frame
      const bestFrame = selectBestFrame(frames);
      setCapturedFrames([bestFrame]);
      setSelectedFrame(bestFrame);
      
      toast.success('Photo captured! Auto-selected best frame.');
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Failed to capture photo');
    } finally {
      setIsCapturing(false);
    }
  };
  
  /**
   * Handle burst capture (2-3 seconds)
   */
  const handleBurstCapture = async () => {
    setIsCapturing(true);
    setBurstProgress(0);
    const frames = [];
    const startTime = Date.now();
    const durationMs = burstDuration * 1000;
    const targetFPS = 8;
    const frameInterval = 1000 / targetFPS;
    
    try {
      burstInterval.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / durationMs) * 100, 100);
        setBurstProgress(progress);
        
        if (elapsed >= durationMs) {
          clearInterval(burstInterval.current);
          finalizeBurst(frames);
          return;
        }
        
        const frame = captureFrame();
        if (frame) frames.push(frame);
      }, frameInterval);
      
    } catch (error) {
      console.error('Burst capture error:', error);
      toast.error('Failed to capture burst');
      setIsCapturing(false);
    }
  };
  
  const finalizeBurst = (frames) => {
    if (frames.length === 0) {
      toast.error('No frames captured');
      setIsCapturing(false);
      return;
    }
    
    // Get top 3 frames
    const scoredFrames = frames
      .map((frame, idx) => ({
        ...frame,
        score: frame.blurScore * 0.7 + (200 - Math.abs(frame.brightnessScore - 128)) * 0.3,
        index: idx
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
    
    setCapturedFrames(scoredFrames);
    setSelectedFrame(scoredFrames[0]);
    setIsCapturing(false);
    
    toast.success(`Captured ${frames.length} frames! Auto-selected best frame.`);
  };
  
  /**
   * Select best frame from array based on sharpness and brightness
   */
  const selectBestFrame = (frames) => {
    if (frames.length === 0) return null;
    
    return frames.reduce((best, current) => {
      const bestScore = best.blurScore * 0.7 + (200 - Math.abs(best.brightnessScore - 128)) * 0.3;
      const currentScore = current.blurScore * 0.7 + (200 - Math.abs(current.brightnessScore - 128)) * 0.3;
      return currentScore > bestScore ? current : best;
    });
  };
  
  const handleConfirm = () => {
    if (selectedFrame) {
      const captureMeta = {
        blurScore: selectedFrame.blurScore,
        brightnessScore: selectedFrame.brightnessScore,
        captureMode,
        framesCaptured: capturedFrames.length
      };
      const captureSource = captureMode === 'burst' ? 'camera_burst' : 'camera_single';
      onCapture(selectedFrame.dataUrl, { captureSource, captureMeta });
      stopCamera();
    }
  };
  
  const handleRetake = () => {
    setCapturedFrames([]);
    setSelectedFrame(null);
    setBurstProgress(0);
  };
  
  // Render quality guidance
  const renderQualityGuidance = () => {
    if (!showGuidance) return null;
    
    const getBlurStatus = () => {
      switch (qualityMetrics.blur) {
        case 'sharp': return { icon: CheckCircle, color: 'text-green-600', text: 'Sharp ✓', bg: 'bg-green-50' };
        case 'slight_blur': return { icon: AlertTriangle, color: 'text-yellow-600', text: 'Slight blur', bg: 'bg-yellow-50' };
        case 'blurry': return { icon: X, color: 'text-red-600', text: 'Blurry - hold steady', bg: 'bg-red-50' };
        default: return { icon: Eye, color: 'text-gray-600', text: 'Checking...', bg: 'bg-gray-50' };
      }
    };
    
    const getBrightnessStatus = () => {
      switch (qualityMetrics.brightness) {
        case 'good': return { icon: CheckCircle, color: 'text-green-600', text: 'Good light ✓', bg: 'bg-green-50' };
        case 'dark': return { icon: Sun, color: 'text-yellow-600', text: 'Low light', bg: 'bg-yellow-50' };
        case 'bright': return { icon: Sun, color: 'text-yellow-600', text: 'Too bright', bg: 'bg-yellow-50' };
        default: return { icon: Sun, color: 'text-gray-600', text: 'Checking...', bg: 'bg-gray-50' };
      }
    };
    
    const getFramingStatus = () => {
      switch (qualityMetrics.framing) {
        case 'good': return { icon: CheckCircle, color: 'text-green-600', text: 'Good framing ✓', bg: 'bg-green-50' };
        case 'too_far': return { icon: Zap, color: 'text-yellow-600', text: 'Move closer', bg: 'bg-yellow-50' };
        default: return { icon: Zap, color: 'text-gray-600', text: 'Checking...', bg: 'bg-gray-50' };
      }
    };
    
    const blurStatus = getBlurStatus();
    const brightnessStatus = getBrightnessStatus();
    const framingStatus = getFramingStatus();
    
    return (
      <div className="absolute top-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 space-y-2 shadow-lg z-10">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Capture Quality</h3>
          <button
            onClick={() => setShowGuidance(false)}
            className="text-xs text-gray-600 hover:text-gray-900"
          >
            Hide
          </button>
        </div>
        
        <div className={`flex items-center gap-2 px-3 py-2 rounded ${blurStatus.bg}`}>
          <blurStatus.icon className={`w-4 h-4 ${blurStatus.color}`} />
          <span className={`text-sm ${blurStatus.color}`}>{blurStatus.text}</span>
        </div>
        
        <div className={`flex items-center gap-2 px-3 py-2 rounded ${brightnessStatus.bg}`}>
          <brightnessStatus.icon className={`w-4 h-4 ${brightnessStatus.color}`} />
          <span className={`text-sm ${brightnessStatus.color}`}>{brightnessStatus.text}</span>
        </div>
        
        <div className={`flex items-center gap-2 px-3 py-2 rounded ${framingStatus.bg}`}>
          <framingStatus.icon className={`w-4 h-4 ${framingStatus.color}`} />
          <span className={`text-sm ${framingStatus.color}`}>{framingStatus.text}</span>
        </div>
      </div>
    );
  };
  
  // If frames captured, show selection UI
  if (capturedFrames.length > 0) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <img
            src={selectedFrame.dataUrl}
            alt="Captured"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        
        {capturedFrames.length > 1 && (
          <div className="bg-white/10 backdrop-blur-sm p-4">
            <p className="text-white text-sm mb-2">Choose best frame:</p>
            <div className="flex gap-2 overflow-x-auto">
              {capturedFrames.map((frame, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedFrame(frame)}
                  className={`relative flex-shrink-0 w-20 h-20 rounded border-2 transition-all ${
                    selectedFrame === frame ? 'border-blue-500 scale-105' : 'border-white/50'
                  }`}
                >
                  <img src={frame.dataUrl} alt={`Frame ${idx + 1}`} className="w-full h-full object-cover rounded" />
                  {selectedFrame === frame && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div className="bg-white p-4 flex gap-3">
          <button
            onClick={handleRetake}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Retake
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Use This Photo
          </button>
        </div>
      </div>
    );
  }
  
  // Camera preview UI
  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Video preview with quality overlay */}
      <div className="relative flex-1">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />
        
        {renderQualityGuidance()}
        
        {!showGuidance && (
          <button
            onClick={() => setShowGuidance(true)}
            className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs text-gray-700"
          >
            Show Guidance
          </button>
        )}
        
        {isCapturing && captureMode === 'burst' && (
          <div className="absolute bottom-24 left-4 right-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Timer className="w-5 h-5 text-blue-600 animate-pulse" />
                <span className="text-sm font-medium text-gray-900">
                  Capturing burst... {Math.round(burstProgress)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-200"
                  style={{ width: `${burstProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="bg-white p-4 space-y-4">
        {/* Mode selection */}
        <div className="flex gap-2">
          <button
            onClick={() => setCaptureMode('single')}
            disabled={isCapturing}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              captureMode === 'single'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${isCapturing ? 'opacity-50' : ''}`}
          >
            Single Photo
          </button>
          <button
            onClick={() => setCaptureMode('burst')}
            disabled={isCapturing}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              captureMode === 'burst'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${isCapturing ? 'opacity-50' : ''}`}
          >
            Burst Mode
          </button>
        </div>
        
        {captureMode === 'burst' && (
          <div className="flex gap-2">
            <button
              onClick={() => setBurstDuration(2)}
              disabled={isCapturing}
              className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-all ${
                burstDuration === 2
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${isCapturing ? 'opacity-50' : ''}`}
            >
              2 seconds
            </button>
            <button
              onClick={() => setBurstDuration(3)}
              disabled={isCapturing}
              className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-all ${
                burstDuration === 3
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${isCapturing ? 'opacity-50' : ''}`}
            >
              3 seconds
            </button>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isCapturing}
            className={`py-3 px-6 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors ${
              isCapturing ? 'opacity-50' : ''
            }`}
          >
            Cancel
          </button>
          <button
            onClick={captureMode === 'single' ? handleSingleCapture : handleBurstCapture}
            disabled={isCapturing}
            className={`flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 ${
              isCapturing ? 'opacity-50' : ''
            }`}
          >
            <Camera className="w-5 h-5" />
            {isCapturing
              ? 'Capturing...'
              : captureMode === 'burst'
              ? `Capture ${burstDuration}s Burst`
              : 'Capture Photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCameraCapture;
